import { action, computed, observable } from 'mobx';
import { computedFn } from 'mobx-utils';

import { missionTypes } from '@/constants/missions';
import { serviceTypes } from '@/constants/services';
import {
  MissionSettingsResponse,
  SettingsResponse,
  SyncPlayerMission,
} from '@/types/api';
import { Missions, MissionTypes, UserMission } from '@/types/models/mission';
import { mapMissions } from '@/utils/mapSettings';
import { ServicesMixin } from '@/utils/servicesMixin';

import QueryStore from './QueryStore';

@ServicesMixin([serviceTypes.QUERY])
export default class MissionsStore {
  query: QueryStore;

  @observable public accessor dialogMission = false;
  @observable public accessor dialogMissionComplete = false;
  @observable public accessor selectedMissionValues: {
    id: number;
    type: MissionTypes;
  } | null = null;

  @observable.ref public accessor allMissions: Record<number, Missions> = {};
  @observable public accessor userMissions: Record<number, UserMission> = {};

  @computed public get selectedMission() {
    if (!this.selectedMissionValues?.type) return null;
    return this.allMissions[this.selectedMissionValues.id];
  }

  @computed public get selectedMissionProcessing() {
    if (!this.selectedMission) return false;
    return !!this.query.get(`mission-${this.selectedMission?.externalId}`).state
      .pending;
  }

  constructor(services?: [QueryStore]) {
    const [queryService] = services!;
    this.query = queryService;
  }

  getUserMission = computedFn(
    (externalId: number): UserMission | null =>
      this.userMissions[externalId] || null,
  );

  getMission = computedFn((id: number) => this.allMissions[id] || null);

  @action.bound public async init({
    missions,
    userMissions,
  }: {
    missions: MissionSettingsResponse[];
    userMissions: SyncPlayerMission[];
  }) {
    this.allMissions = { ...this.allMissions, ...mapMissions(missions) };
    this.setUserMissions(userMissions);
  }

  @action.bound public setUserMissions(list: SyncPlayerMission[]) {
    this.userMissions = list.reduce<Record<number, UserMission>>(
      (missions, mission) => ({
        ...missions,
        [mission.external_id]: {
          externalId: mission.external_id,
        },
      }),
      {},
    );
  }

  @action.bound public setSettings = (settings: SettingsResponse) => {
    this.allMissions = {
      ...this.allMissions,
      ...mapMissions(settings.mission),
    };
  };

  @action.bound public setUserMission(externalId: number) {
    this.userMissions[externalId] = { externalId };
  }

  @action.bound public setDialogMission(value: boolean) {
    this.dialogMission = value;
  }

  @action.bound public setDialogMissionComplete(value: boolean) {
    this.dialogMissionComplete = value;
  }

  @action.bound public selectMission = (mission: Missions | null) => {
    if (!mission) return;
    this.selectedMissionValues = { id: mission?.id, type: mission?.type };
  };

  @action.bound public onAction = (mission: Missions) => {
    if (mission.type === missionTypes.channel && mission.link) {
      return;
    }

    if (mission.type === missionTypes.link && mission.link) {
      if (mission.link.includes('t.me')) {
        return;
      }

      return;
    }
  };
}
